import React from 'react';
import Alert from './Alert';
import { Button, ButtonColors } from './Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

type Props = {
  isOpen: boolean;
  title?: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  error?: {
    active: boolean;
    title?: string;
    text: string;
    cancelText?: string;
  };
  confirm: (e?: any) => void;
  close: (e?: any) => void;
  loading?: boolean;
  icon?: React.ReactNode;
  primaryColor?: string;
  noColor?: boolean;
  children?: React.ReactNode;
  confirmDisabled?: boolean;
  isDelete?: boolean;
  bigger?: boolean;
  focusOnCancel?: boolean;
  hideConfirm?: boolean;
};

export const ConfirmModal: React.FC<Props> = ({
  title = 'Are you sure?',
  description,
  confirmText = 'Yes',
  cancelText = 'Cancel',
  error = { active: false, title: 'Error', text: '', cancelText: 'Close' },
  confirm,
  close,
  isOpen,
  loading = false,
  icon,
  primaryColor,
  noColor,
  children,
  confirmDisabled = false,
  isDelete = false,
  bigger,
  focusOnCancel,
  hideConfirm = false,
}) => {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        size={bigger ? 'xl' : 'default'}
        className="sm:max-w-md data-[size=xl]:sm:max-w-3xl bg-white dark:bg-dark-gray-900"
      >
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-gray-50">
            {title}
          </DialogTitle>

          {error.active && (
            <div className="w-full my-8">
              <Alert title={error.title} error={error.text} />
            </div>
          )}
          {!error.active && !!description && (
            <DialogDescription asChild>
              <div className="text-sm text-gray-500 mt-2">{description}</div>
            </DialogDescription>
          )}
        </DialogHeader>

        {children && <div className="px-6">{children}</div>}

        <DialogFooter className="bg-white dark:bg-dark-gray-900 flex-shrink-0">
          <Button
            text={error.active ? error.cancelText || cancelText : cancelText}
            onClick={close}
            color={ButtonColors.plain}
            autoFocus={focusOnCancel}
          />
          {!hideConfirm && (
            <Button
              text={confirmText}
              onClick={confirm}
              loading={loading}
              disabled={confirmDisabled || loading || error.active}
              color={
                isDelete
                  ? ButtonColors.red
                  : noColor || primaryColor
                    ? ButtonColors.plainWithBorder
                    : ButtonColors.primary
              }
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
